import { addDays, isAfter } from 'date-fns';
import { logAnalyticsEvent } from '@/lib/analytics';
import { BACKUP_INTERVAL_DAYS } from '@/lib/autoBackup';
import {
    applyBackupContent,
    createBackupContent,
    type RestoreBackupOptions,
    type RestoreResult,
} from '@/lib/backupService';
import {
    getBackupKdfParams,
    getBackupKey,
    getBackupSalt,
    hasBackupPassphrase,
} from '@/lib/backupPassphrase';
import {
    DriveApiError,
    DriveNetworkError,
    type DriveFile,
    deleteBackup,
    downloadBackup,
    ensureBackupFolder,
    listBackups,
    rotateBackups,
    uploadBackup,
} from '@/lib/googleDrive/driveClient';
import { connectGoogleDrive, disconnectGoogleDrive, getAccessToken } from '@/lib/googleDrive/googleAuth';
import { syncBeforeSnapshot } from '@/lib/sync/sync';
import { useDriveBackupStore } from '@/stores/useDriveBackupStore';

/**
 * Orquestra o backup no Google Drive (ver ADR 0027): cifra o snapshot (reusa
 * createBackupContent), sobe pela Drive REST API, rotaciona e marca. Oportunístico —
 * roda quando o app é usado, sem cron de background. Pro gateia LIGAR/automático;
 * restaurar é sempre grátis.
 */

const AUTH_ERROR_MSG = 'Sua conexão com o Google expirou. Reconecte a conta para voltar a fazer backup.';
// Antes chegava cru ao toast do Conectar: "fetch failed: Fetch request has been canceled".
const NETWORK_ERROR_MSG = 'O Google Drive não respondeu. Confira a internet e tente de novo.';

function errorMessage(error: unknown): string {
    if (error instanceof DriveApiError) {
        return error.isAuthError ? AUTH_ERROR_MSG : `Falha ao acessar o Drive (${error.status}).`;
    }
    if (error instanceof DriveNetworkError) return NETWORK_ERROR_MSG;
    return error instanceof Error ? error.message : 'Falha no backup para o Drive.';
}

/** O mesmo texto humano das falhas daqui, para quem fala com o Drive por fora (assistente do "Ativar"). */
export function describeDriveError(error: unknown): string {
    return errorMessage(error);
}

export function isDriveBackupDue(lastBackupAt: string | null): boolean {
    if (!lastBackupAt) return true;
    const next = addDays(new Date(lastBackupAt), BACKUP_INTERVAL_DAYS);
    return isAfter(new Date(), next);
}

export interface RunDriveBackupResult {
    ok: boolean;
    error?: string;
    /** true = token caiu/revogado → a UI oferece "Reconectar". */
    isAuthError?: boolean;
}

/** Gera o backup cifrado, sobe pro Drive e rotaciona. Requer senha configurada. */
export async function runDriveBackup(): Promise<RunDriveBackupResult> {
    const store = useDriveBackupStore.getState();
    if (!store.connectedEmail) return { ok: false, error: 'Conecte uma conta Google primeiro.' };
    if (!(await hasBackupPassphrase())) return { ok: false, error: 'Configure uma senha de backup primeiro.' };

    const key = await getBackupKey();
    const salt = await getBackupSalt();
    if (!key || !salt) return { ok: false, error: 'Não foi possível ler a chave de cifra.' };
    const params = await getBackupKdfParams();

    // Sync ANTES do snapshot (não depois): põe o espaço em dia pra este backup nascer contido
    // nele. Devolve null se o sync está desligado/sem rede — o backup roda igual, só sem carimbo.
    const syncedAt = await syncBeforeSnapshot();

    // Backup no Drive é SEMPRE cifrado (ADR 0028) — nunca sobe JSON aberto.
    const { content, filename } = await createBackupContent({ key, salt, params }, { syncedAt });

    try {
        const token = await getAccessToken();
        let folderId = store.folderId;
        if (!folderId) {
            folderId = await ensureBackupFolder(token);
            useDriveBackupStore.getState().setFolder(folderId);
        }
        await uploadBackup(token, folderId, filename, content);
        await rotateBackups(token, folderId, store.keepVersions);
        useDriveBackupStore.getState().markBackedUp();
        void logAnalyticsEvent('backup_created', { kind: 'auto' });
        return { ok: true };
    } catch (error) {
        const message = errorMessage(error);
        const store = useDriveBackupStore.getState();
        store.setError(message);
        // Pasta "Kontto" sumiu do Drive (usuário apagou/lixeira) → o parent salvo virou 404.
        // Zera o folderId pra próxima rodada re-chamar ensureBackupFolder e se auto-curar,
        // em vez de falhar pra sempre calado mirando numa pasta morta.
        if (error instanceof DriveApiError && error.isNotFoundError) store.setFolder(null);
        return { ok: false, error: message, isAuthError: error instanceof DriveApiError && error.isAuthError };
    }
}

/**
 * Chamado quando o app é usado (boot/foreground/background): roda o backup se ligado,
 * Pro, conectado, com senha e vencido. Silencioso — nunca abre UI. Ver ADR 0027 (gatilho).
 */
export async function maybeRunDriveBackup(isPro: boolean): Promise<void> {
    const { enabled, connectedEmail, lastBackupAt } = useDriveBackupStore.getState();
    if (!enabled || !isPro || !connectedEmail) return;
    if (!isDriveBackupDue(lastBackupAt)) return;

    try {
        // Dentro do try pela mesma razão do maybeSyncNow: Keychain bloqueado no
        // acordar do iOS lança, e os callers usam `void` — seria unhandled rejection.
        if (!(await hasBackupPassphrase())) return;
        const result = await runDriveBackup();
        if (!result.ok) console.warn('[driveBackup] falha no backup automático:', result.error);
    } catch (error) {
        console.warn('[driveBackup] falha no backup automático:', error);
    }
}

// ─── Conectar / desconectar ──────────────────────────────────

/** Login interativo + descobre/cria a pasta. Persiste a conexão. */
export async function connectDrive(): Promise<{ ok: boolean; email?: string; error?: string }> {
    try {
        const email = await connectGoogleDrive();
        const token = await getAccessToken();
        const folderId = await ensureBackupFolder(token);
        const store = useDriveBackupStore.getState();
        store.setConnection(email, folderId);
        // Reconectar É a recuperação do erro de auth: limpa o lastError velho pra o banner
        // vermelho sumir e o alerta "backup parou" parar de disparar antes do próximo backup.
        store.setError(null);
        return { ok: true, email };
    } catch (error) {
        console.warn('[connectDrive] falhou:', error instanceof Error ? error.message : error);
        return { ok: false, error: errorMessage(error) };
    }
}

export async function disconnectDrive(): Promise<void> {
    await disconnectGoogleDrive();
    useDriveBackupStore.getState().reset();
}

// ─── Restore (sempre grátis) ─────────────────────────────────

/** O backup mais recente no Drive (pra oferecer no celular novo). null se não houver. */
export async function getLatestDriveBackup(): Promise<DriveFile | null> {
    const token = await getAccessToken();
    const folderId = useDriveBackupStore.getState().folderId ?? (await ensureBackupFolder(token));
    const files = await listBackups(token, folderId);
    return files[0] ?? null;
}

/** Lista todas as versões no Drive (pra tela de histórico/escolha). */
export async function listDriveBackups(): Promise<DriveFile[]> {
    const token = await getAccessToken();
    const folderId = useDriveBackupStore.getState().folderId ?? (await ensureBackupFolder(token));
    return listBackups(token, folderId);
}

/** Apaga um ponto de restauração no Drive — só por escolha explícita do usuário (tela 2b). */
export async function deleteDriveBackup(fileId: string): Promise<{ ok: boolean; error?: string }> {
    try {
        const token = await getAccessToken();
        await deleteBackup(token, fileId);
        return { ok: true };
    } catch (error) {
        return { ok: false, error: errorMessage(error) };
    }
}

/** Baixa um backup do Drive e aplica (substitui a base local — destrutivo). */
export async function restoreFromDrive(fileId: string, options?: RestoreBackupOptions): Promise<RestoreResult> {
    try {
        const token = await getAccessToken();
        const content = await downloadBackup(token, fileId);
        return await applyBackupContent(content, options);
    } catch (error) {
        return { success: false, error: errorMessage(error) };
    }
}
